import { BookingStatus } from 'store/bookings/types';
import { TransactionDetails } from 'store/transactions/types';

// A cancelled/denied booking is already terminal.
const TERMINAL_BOOKING_STATUSES: BookingStatus[] = [
  BookingStatus.CANCELED,
  BookingStatus.LATE_CANCEL,
  BookingStatus.DENIED_AVAILABLE,
  BookingStatus.DENIED_UNAVAILABLE,
];

// Cancelled specifically — NOT the denied statuses above, which are terminal but are not a
// cancellation and must not claim the receipt's cancelled copy.
const CANCELLED_BOOKING_STATUSES: BookingStatus[] = [BookingStatus.CANCELED, BookingStatus.LATE_CANCEL];

/**
 * Whether the booking ITSELF is cancelled, as opposed to this session having just cancelled it.
 *
 * The receipt's cancelled presentation used to hang entirely off `patchTransaction` — the redux state
 * of a cancel performed in this session — which makes it a post-action confirmation rather than a
 * rendering of the booking's state. Arriving at an already-cancelled booking any other way (the
 * activity-center deep link is the one that matters) left no patch state, so the same screen rendered
 * as an ordinary receipt with no indication it had been cancelled at all.
 */
export const isBookingCancelled = (status?: string): boolean =>
  !!status && CANCELLED_BOOKING_STATUSES.includes(status as BookingStatus);

// Whether a booking can still be checked in to / cancelled from the receipt. A past booking's
// transaction status never changes once it has ended (matches activity center), so a terminal
// booking must be recognised by TIME as well as status — not status alone. A booking that has
// already ended, or is in a terminal (cancelled/denied) state, offers neither action.
export const isBookingActionable = (transaction?: TransactionDetails): boolean => {
  const status = transaction?.details?.status;
  const endAt = transaction?.details?.end_at;
  const hasBookingEnded = !!endAt && new Date(endAt).getTime() < Date.now();

  return !hasBookingEnded && !(status && TERMINAL_BOOKING_STATUSES.includes(status));
};
